import { IError, RootState, ThunkDispatch } from "core-integration/src/store";
import { getNextEvent } from "core-integration/src/store/events/reducers";
import { getRules } from "core-integration/src/store/game/reducers";
import { IRules } from "core-integration/src/store/game/types";
import { getJoinPublicLeagueError } from "core-integration/src/store/leagues/reducers";
import { joinPublicLeague } from "core-integration/src/store/leagues/thunks";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router";
import { Box } from "rebass/styled-components";
import Alert from "../Alert";
import Button from "../Button";
import Copy from "../Copy";
import { Main, Wrapper } from "../Layout";
import SubHeading from "../SubHeading";
import Title from "../Title";

const JoinPublic: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch<ThunkDispatch>();
  const navigate = useNavigate();

  const [joinType, setJoinType] = useState<"c" | "h" | null>(null);
  const [attemptedJoin, setAttemptedJoin] = useState(false);

  const rules = useSelector((state: RootState) => getRules(state)) as IRules;

  const error = useSelector((state: RootState) =>
    getJoinPublicLeagueError(state),
  ) as IError | null;

  const nextEvent = useSelector(getNextEvent);

  useEffect(() => {
    if (!attemptedJoin || !joinType) return;

    if (error) {
      setAttemptedJoin(false);
      return;
    }

    const destination = nextEvent ? "/leagues" : "/";
    navigate(destination, { replace: !nextEvent });
    setAttemptedJoin(false);
    setJoinType(null);
  }, [error, nextEvent, attemptedJoin, joinType, navigate]);

  const handleJoin = async (type: "c" | "h") => {
    await dispatch(joinPublicLeague({ scoring: type }));
    setJoinType(type);
    setAttemptedJoin(true);
  };

  return (
    <Wrapper>
      <Main>
        <Copy>
          <Title>{t("joinPublic.title", "Join public league")}</Title>
          {error ? (
            error.badRequest &&
            error.badRequest.non_field_errors &&
            error.badRequest.non_field_errors[0].code ===
              "public_league_max_exceeded" ? (
              <Box mb={2}>
                <Alert type="error">
                  {t(
                    "joinPublic.error",
                    "You may only enter {{ publicLeagueMax }} public leagues",
                    { publicLeagueMax: rules.league_join_public_max },
                  )}
                </Alert>
              </Box>
            ) : error?.badRequest?.non_field_errors[0].code ===
              "past_last_event" ? (
              <Box mb={2}>
                <Alert type="error">
                  {t(
                    "joinPublic.lastEventError",
                    "You cannot join public leagues after season has ended.",
                  )}
                </Alert>
              </Box>
            ) : (
              <Box mb={2}>
                <Alert type="error">
                  {error?.badRequest?.non_field_errors?.[0]?.message}
                </Alert>
              </Box>
            )
          ) : (
            ""
          )}
          <Box mb={4}>
            <p>
              {t(
                "joinPublic.p1",
                "Public leagues allow you to compete against {{ maxSizePublicClassic }} randomly assigned game players in a classic league. You can join up to {{ leagueJoinPublicMax }} public leagues.",
                {
                  maxSizePublicClassic: rules.league_max_size_public_classic,
                  leagueJoinPublicMax: rules.league_join_public_max,
                },
              )}
            </p>
            <p>
              <strong>
                {t(
                  "joinPublic.p2",
                  "Note, you can't remove your team from a public league after the league has started, once the challenge is on there's no quitting.",
                )}
              </strong>
            </p>
          </Box>

          <Box mb={4}>
            <SubHeading>
              {t("joinPublic.classic.title", "Classic scoring")}
            </SubHeading>
            <p>
              {t(
                "joinPublic.classic.p1",
                "In a league with classic scoring, teams are ranked based on their total points in the game. You can join or leave a league with classic scoring at any point during the season",
              )}
            </p>
            <Button onClick={() => handleJoin("c")}>
              {t("joinPublic.classic.button", "Join classic league")}
            </Button>
          </Box>
        </Copy>
      </Main>
    </Wrapper>
  );
};

export default JoinPublic;
